/* eslint-disable no-nested-ternary */
import { Delete } from '@mui/icons-material';
import { Box, FormControlLabel, Grid, IconButton, Switch, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { Dish, DishComponent, Ingredient, SemiProduct } from '../../apiTypes';
import { t } from '../../constants/translations';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { getInitMap } from '../../utils/map';
import AddIngredientButton from '../AddIngredientButton';
import Image from '../Image';
import FormQntInput from './FormQntInput';
import FormSelect from './FormSelect';

type FormType = {
  components: DishComponent[];
};

export type FormComponentsTableProps = {
  options: (Ingredient & { maxQnt?: number })[];
  form: UseFormReturn<Dish | SemiProduct>;
  name?: unknown;
  shouldSelectStore?: boolean;
  handleAddStore?: () => void;
  small?: boolean;
  title?: string;
  disablePackingInit?: boolean;
};

const FormComponentsTable = ({
  options,
  name = 'components',
  form,
  shouldSelectStore,
  small = false,
  handleAddStore,
  title,
  disablePackingInit = false,
}: FormComponentsTableProps) => {
  const { lang } = useLocale();
  const { stores, storesMap } = useCafe();

  const [disablePacking, setDisablePacking] = useState(disablePackingInit);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: name as 'components',
    keyName: 'key',
  });

  const filteredOptions = options.filter((o) => !fields.some((c) => c.id === o.id));

  const optionsMap = useMemo(
    () => options.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), getInitMap<Ingredient & { maxQnt?: number }>()),
    [options]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
        {fields.length > 0 && (
          <Typography variant="body2" color={(t) => t.palette.text.secondary}>
            {title}
          </Typography>
        )}

        {fields.length > 0 && (
          <FormControlLabel
            control={
              <Switch size="small" checked={!disablePacking} onChange={(e) => setDisablePacking(!e.target.checked)} />
            }
            label={t.display_packaging[lang]}
          />
        )}
      </Box>

      {fields.length > 0 && shouldSelectStore && !small && (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={5}>
            <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
              {t.component[lang]}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
              {t.store[lang]}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
              {t.quantity[lang]}
            </Typography>
          </Grid>
        </Grid>
      )}
      {fields.map((field, index) => (
        <Grid
          key={field.key}
          container
          alignItems="center"
          sx={{ p: 0.5, borderRadius: 1, ':hover': { bgcolor: (t) => t.palette.background.default } }}
          data-testid={`components.${index}`}
        >
          <Grid
            item
            xs={shouldSelectStore || !disablePacking ? 12 : 7}
            sm={shouldSelectStore || !disablePacking ? 5 : 7}
          >
            <Box gap={1} sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
              <Image src={optionsMap[field.id]?.imageUrl} />
              <Box>
                <Typography sx={{ lineHeight: 0.8 }}>{optionsMap[field.id]?.name}</Typography>

                {optionsMap[field.id]?.maxQnt !== undefined && (
                  <Typography variant="caption" sx={{ lineHeight: 0.8 }} color={(t) => t.palette.text.secondary}>{`${
                    t.available[lang]
                  }: ${optionsMap[field.id]?.maxQnt} ${t[optionsMap[field.id]?.unit || 'g'][lang]}`}</Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {shouldSelectStore && (
            <Grid item xs={6} sm={3} sx={{ pr: 1, mb: { xs: disablePacking ? 0 : 1, sm: 0 } }}>
              <FormSelect
                name={`${name}.${index}.store`}
                size="small"
                options={stores.map((w) => w.id)}
                renderOption={(o) => storesMap[o]?.name}
                onAddNew={handleAddStore}
                addButtonText={t.add_store[lang]}
              />
            </Grid>
          )}

          <Grid
            item
            xs={shouldSelectStore ? (disablePacking ? 6 : 12) : 5}
            sm={shouldSelectStore ? 4 : disablePacking ? 5 : 7}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FormQntInput
              ingredient={{ unit: optionsMap[field.id]?.unit || 'g', qpp: optionsMap[field.id]?.qpp }}
              name={`${name}.${index}.qnt`}
              disablePacking={disablePacking}
            />
            <IconButton sx={{ width: 32, height: 32, ml: 1, flexShrink: 0 }} size="small" onClick={() => remove(index)}>
              <Delete />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Box sx={{ maxWidth: 260, my: small ? 0 : 0.5 }}>
        <AddIngredientButton
          options={filteredOptions}
          small={small}
          onAdd={(value) =>
            append({
              type: 'product',
              id: value.id,
              store: stores[0]?.id || '',
              qnt: 0,
            })
          }
        />
      </Box>
    </Box>
  );
};

export default FormComponentsTable;
